import React from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import ChevronLeftOutlined from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import { cn } from "../ds/cn";
import { getNavForRole, activeNavKey } from "./navConfig";
import { useShell } from "./ShellContext";
import images from "../../assets/images";
import styles from "./Sidebar.module.scss";

/**
 * Deep-slate ops sidebar — expands to labels, collapses to icons.
 * Preference is owned by ShellContext (localStorage). On mobile it is an
 * off-canvas overlay opened from the TopBar.
 */
export default function Sidebar({ role, onNavigate }) {
  const history = useHistory();
  const location = useLocation();
  const { collapsed, toggleCollapsed, isMobile, closeMobileNav } = useShell();
  const items = getNavForRole(role);
  const activeKey = activeNavKey(items, location.pathname);
  // On mobile always show labels while the drawer is open.
  const showCollapsed = collapsed && !isMobile;

  const go = (path) => {
    if (onNavigate) onNavigate(path);
    else history.push(path);
    if (isMobile) closeMobileNav();
  };

  return (
    <aside
      className={cn(styles.sidebar, showCollapsed && styles.collapsed)}
      aria-label="Primary"
      data-collapsed={showCollapsed ? "true" : "false"}
    >
      <div className={styles.brand}>
        <img
          src={showCollapsed ? images.AppLogoMark : images.AppLogo}
          alt="Cybrbase"
          className={styles.brandLogo}
        />
      </div>

      <nav className={styles.nav}>
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              className={cn(styles.navItem, active && styles.active)}
              onClick={() => go(item.path)}
              title={showCollapsed ? item.label : undefined}
              aria-current={active ? "page" : undefined}
            >
              <span className={styles.navIcon}>
                <Icon fontSize="small" />
              </span>
              <span className={styles.navLabel}>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {!isMobile ? (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? (
              <ChevronRightOutlined fontSize="small" />
            ) : (
              <>
                <ChevronLeftOutlined fontSize="small" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      ) : null}
    </aside>
  );
}

Sidebar.propTypes = {
  role: PropTypes.string,
  onNavigate: PropTypes.func,
};
